import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const StatusCategoriesContext = createContext();
 
export const useStatusCategories = () => {
  const context = useContext(StatusCategoriesContext);
  if (!context) {
    throw new Error('useStatusCategories must be used within a StatusCategoriesProvider');
  }
  return context;
};

export const StatusCategoriesProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [setApi] = useState(null);
  const { handleApiError } = useError();
  const [statusCategories, setStatusCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/status-categories');

  const getStatusCategories = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      
      if(filters?.type){
        query += '&type='+filters?.type;
      } else{
        const type = searchParams.get('type') || '';
        query += '&type='+type;
      }
      
      let response = await apiInstance.call('status-categories?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          query += '&active='+filters?.active;
        } else{
          const active = searchParams.get('active') || '';
          query += '&active='+active;
        }
        if(filters?.type){
        query += '&type='+filters?.type;
        } else{
        const type = searchParams.get('type') || '';
        query += '&type='+type;
        }

        response = await apiInstance.call('status-categories?page=' + 0 +''+query);  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setStatusCategories(response.data.statusCategories || []);
       
        if(filters){
          return response.data.statusCategories;
        }
      } else {
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch status categories information'
        });
      }
    } catch (error) {
      // Handle error silently
    } finally {
      // Cleanup if needed
    }
  }, [handleApiError, paginate, getToken]);

  const getStatusCategoryByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`status-categories/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      let statusCategories = {'data':[response.data.statusCategory]}
      setStatusCategories(statusCategories || []);
    } 
  }, [getToken]);

  useEffect(() => {
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/status-categories/edit/'+id))){
        if (getToken()) {
          await getStatusCategoryByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getStatusCategoryByIdApi, searchParams, setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
          await getStatusCategories(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getStatusCategories, searchParams, initialized, hideLoading, setApi]);

  const getStatusCategoriesAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-status-categories-additional-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get status categories data'
      });
    } 
  }, [getToken, handleApiError]);

  const addStatusCategory = async (data) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {
      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...data };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('status-categories', 'POST', formData, true, true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the status categories list
        await getStatusCategories();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Status Category'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create status category'
      });
      return { status: 500, error: error.message };
    }
  };

  const getStatusCategory = (id) => {
    return statusCategories?.data?.find(statusCategory => Number(statusCategory.id) === Number(id));
  };

  const getStatusCategoryById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`status-categories/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.statusCategory };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch status category details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch status category details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateStatusCategory = async (id, updatedData) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {
      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      formData.append('_method', 'PUT');

      const response = await apiInstance.call(`status-categories/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the status categories list
        await getStatusCategories();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update status category'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update status category'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteStatusCategory = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`status-categories/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the status categories list
        await getStatusCategories();
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete status category'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete status category'
      });
      return { status: 500, error: error.message };
    }
  };

  const value = {
    statusCategories,
    getStatusCategories,
    getStatusCategoryById,
    getStatusCategory,
    addStatusCategory,
    createStatusCategory: addStatusCategory,
    updateStatusCategory,
    deleteStatusCategory,
    getStatusCategoriesAdditionalData,
    isLoading
  };

  return (
    <StatusCategoriesContext.Provider value={value}>
      {children}
    </StatusCategoriesContext.Provider>
  );
};

export default StatusCategoriesContext;
